import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError, apiRequest } from "../../api/client";
import type { components } from "../../api/generated/schema";
import {
  isRepairNoResultStatus,
  type ConflictCertificate,
  type SchedulingAlternativeHistory,
  type SchedulingCandidate,
  type SchedulingJobStatus,
  type RepairSessionStatus,
  type SchedulingPrecheckResult
} from "../../api/statelessClient";
import { useDialogFocus } from "../../app/useDialogFocus";
import { getProjectRepository } from "../../data/projectRepository";
import { ProjectRevisionConflictError } from "../../storage/projectStore";
import {
  SchedulingPrecheckStorageError,
  type SchedulingPrecheckRecordV1
} from "../../storage/schedulingPrecheckStore";
import { ConfirmModal } from "../catalog/CatalogPages";
import { ContextHeader } from "../shared/project-context";
import "./projects.css";
import {
  clearSchedulingJobTicket,
  createSchedulingJobTicket,
  readSchedulingJobTicket,
  writeSchedulingJobTicket,
  type SchedulingJobTicket
} from "./schedulingJobTicket";
import type { ProjectDetail } from "./types";
import { RelaxedCandidatePanel } from "./RelaxedCandidatePanel";
import { RepairSessionPanel } from "./RepairSessionPanel";
import {
  clearRepairSessionTicket,
  nextRepairRoundTicket,
  readRepairSessionTicket,
  writeRepairSessionTicket,
  type RepairSessionTicket
} from "./repairSessionTicket";
import {
  demoClearSummary,
  useDemoDataClear
} from "./useDemoDataClear";

interface SchedulingSuggestion {
  code: string;
  message: string;
}

interface SchedulingIssue {
  code: string;
  message: string;
  field_path: string | null;
  suggestions: SchedulingSuggestion[];
}

const CONFLICT_ANALYZER_VERSION = 2;

type GenerateResult = Omit<
  components["schemas"]["GenerateResponse"],
  "outcome"
> & {
  outcome:
    | components["schemas"]["GenerateResponse"]["outcome"]
    | "FEASIBLE"
    | "RELAXED_CANDIDATE"
    | "NO_ACCEPTABLE_ALTERNATIVE";
  course_adjacency_relaxed?: boolean;
  adjacency_requested_pairs?: number;
  adjacency_achieved_pairs?: number;
  can_relax_course_adjacency?: boolean;
  candidate?: SchedulingCandidate | null;
  scheduled_lessons?: number;
  required_lessons?: number;
  attempt_level?: number;
  actual_relaxations?: components["schemas"]["SchedulingRelaxationEvent"][];
  ready?: boolean;
  input_fingerprint?: string;
  conflict_analyzer_version?: number;
  conflict_certificate?: ConflictCertificate | null;
};

type SchedulingJobView = SchedulingJobStatus<GenerateResult>;

/** 旅程四段的进度只能由 ProjectDetail 现有事实推导。 */
interface JourneyStep {
  title: string;
  note: string;
  done: boolean;
}

interface FocusAction {
  href: string;
  label: string;
}

interface FocusSuggestion {
  title: string;
  /** schedule 表示唯一主按钮是当前预检/生成动作。 */
  primary: "schedule" | FocusAction;
  secondary?: FocusAction;
}

interface DemoDataResult {
  condition_revision: number;
  timetable_revision: number;
  module_counts: Record<string, number>;
  cleared_plan_count: number;
  timetable_preserved: boolean;
}

export function ProjectHome({ projectId }: { projectId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const initialTicket = readSchedulingJobTicket(projectId);
  const initialRepairTicket = readRepairSessionTicket(projectId);
  const [schedulingOpen, setSchedulingOpen] = useState(
    initialTicket !== null || initialRepairTicket !== null
  );
  const [schedulingTicket, setSchedulingTicket] =
    useState<SchedulingJobTicket | null>(initialTicket);
  const [schedulingJob, setSchedulingJob] = useState<SchedulingJobView | null>(null);
  const [generateResult, setGenerateResult] = useState<GenerateResult | null>(null);
  const [precheckRecord, setPrecheckRecord] =
    useState<SchedulingPrecheckRecordV1 | null>(null);
  const [precheckRecordLoaded, setPrecheckRecordLoaded] = useState(false);
  const [schedulingError, setSchedulingError] = useState<unknown>(null);
  const [schedulingRetryVersion, setSchedulingRetryVersion] = useState(0);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [generatingPrechecked, setGeneratingPrechecked] = useState(false);
  const [acceptingCandidate, setAcceptingCandidate] = useState(false);
  const [replanningCandidate, setReplanningCandidate] = useState(false);
  const [candidateNotice, setCandidateNotice] = useState<string | null>(null);
  const [repairTicket, setRepairTicket] = useState<RepairSessionTicket | null>(
    initialRepairTicket
  );
  const [repairSession, setRepairSession] = useState<RepairSessionStatus | null>(null);
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairError, setRepairError] = useState<unknown>(null);
  const [demoConfirmOpen, setDemoConfirmOpen] = useState(false);
  const [demoMessage, setDemoMessage] = useState<string | null>(null);
  const handledSchedulingJob = useRef<string | null>(null);
  const schedulingProgressNoticeRef = useRef<HTMLButtonElement | null>(null);
  const project = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => (await getProjectRepository()).read(projectId)
  });
  const demoData = useMutation({
    mutationFn: (confirmReplace: boolean) => {
      if (!project.data) throw new Error("课表项目尚未加载完成");
      return apiRequest<DemoDataResult>(`/projects/${projectId}/demo-data`, {
        method: "POST",
        body: JSON.stringify({
          expected_condition_revision: project.data.condition_revision,
          expected_timetable_revision: project.data.timetable_revision,
          confirm_replace: confirmReplace
        })
      });
    },
    onSuccess: async (result) => {
      setDemoConfirmOpen(false);
      setDemoMessage(
        result.timetable_preserved
          ? "模拟数据已填入；原有课表已保留，请重新编制任课计划后排课。"
          : "六项模拟基础资料已填入，可以继续编制任课计划。"
      );
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(projectId)
      });
    },
    onError: (error, confirmReplace) => {
      if (
        !confirmReplace &&
        error instanceof ApiError &&
        error.code === "CONFIRMATION_REQUIRED"
      ) {
        setDemoConfirmOpen(true);
      }
    }
  });
  const demoClear = useDemoDataClear(projectId, "BASIC", (result) => {
    setDemoMessage(
      result.counts.cleared > 0
        ? `已清理 ${result.counts.cleared} 条未编辑的基础模拟数据；人工改动、依赖项和来源不确定的数据均已保留。`
        : "没有可安全清理的基础模拟数据，现有数据未发生变化。"
    );
  });

  useEffect(() => {
    if (!project.data) return;
    let cancelled = false;
    setPrecheckRecordLoaded(false);
    void getProjectRepository()
      .then((repository) => repository.precheckRecord(projectId))
      .then((record) => {
        if (!cancelled) setPrecheckRecord(record);
      })
      .catch((error) => {
        if (!cancelled) setSchedulingError(error);
      })
      .finally(() => {
        if (!cancelled) setPrecheckRecordLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    project.data?.condition_revision,
    project.data?.timetable_revision,
    projectId
  ]);

  useEffect(() => {
    if (!schedulingTicket || !project.data) return;
    const ticket = schedulingTicket;
    if (
      ticket.conditionRevision !== project.data.condition_revision ||
      ticket.timetableRevision !== project.data.timetable_revision
    ) {
      clearSchedulingJobTicket(projectId, ticket.jobId);
      setSchedulingTicket(null);
      setSchedulingJob(null);
      setGenerateResult(null);
      setSchedulingError(
        new Error("排课条件或课表已变化，旧备选已失效，请按当前条件重新排课")
      );
      setSchedulingOpen(true);
      void getProjectRepository().then(async (repository) => {
        for (const item of [ticket, ticket.fallback].filter(Boolean) as Array<{
          jobId: string;
          accessToken: string;
        }>) {
          try {
            await repository.acknowledgeScheduling(item.jobId, item.accessToken);
          } catch {
            // 已失效的加密结果会按七天保留规则自动清理。
          }
        }
      });
      return;
    }
    let cancelled = false;
    let retryTimer: number | null = null;
    const controller = new AbortController();

    const poll = async (): Promise<void> => {
      try {
        const repository = await getProjectRepository();
        const restoreFallback = async (notice: string): Promise<boolean> => {
          if (!ticket.fallback) return false;
          try {
            const fallbackStatus = await repository.schedulingStatus<GenerateResult>(
              ticket.fallback.jobId,
              ticket.fallback.accessToken,
              controller.signal
            );
            const fallbackResult = fallbackStatus.response?.result;
            if (
              fallbackStatus.status !== "COMPLETED" ||
              fallbackResult?.outcome !== "RELAXED_CANDIDATE" ||
              !fallbackResult.candidate
            ) {
              return false;
            }
            try {
              await repository.acknowledgeScheduling(
                ticket.jobId,
                ticket.accessToken,
                controller.signal
              );
            } catch {
              // 当前终态任务可以按七天保留规则自动清理。
            }
            const restoredTicket: SchedulingJobTicket = {
              contractVersion: ticket.contractVersion,
              operation: ticket.operation,
              jobId: ticket.fallback.jobId,
              accessToken: ticket.fallback.accessToken,
              conditionRevision: ticket.conditionRevision,
              timetableRevision: ticket.timetableRevision,
              searchMode: ticket.searchMode,
              ...(ticket.alternativeHistory
                ? { alternativeHistory: ticket.alternativeHistory }
                : {}),
              submissionConfirmed: true
            };
            writeSchedulingJobTicket(projectId, restoredTicket);
            handledSchedulingJob.current = restoredTicket.jobId;
            setSchedulingTicket(restoredTicket);
            setSchedulingJob(fallbackStatus);
            setGenerateResult(fallbackResult);
            setCandidateNotice(notice);
            setReplanningCandidate(false);
            setSchedulingError(null);
            return true;
          } catch {
            return false;
          }
        };
        let status: SchedulingJobView;
        try {
          status = await repository.schedulingStatus<GenerateResult>(
            ticket.jobId,
            ticket.accessToken,
            controller.signal
          );
        } catch (error) {
          if (!(error instanceof ApiError) || error.code !== "SCHEDULING_JOB_NOT_FOUND") {
            throw error;
          }
          if (ticket.submissionConfirmed) {
            if (
              await restoreFallback(
                "寻找其他备选的任务已过期，继续展示上一份备选。"
              )
            ) {
              return;
            }
            clearSchedulingJobTicket(projectId, ticket.jobId);
            setSchedulingTicket(null);
            setSchedulingError(new Error("排课结果已过期，请重新提交排课任务"));
            setSchedulingOpen(true);
            return;
          }
          if (
            project.data.condition_revision !== ticket.conditionRevision ||
            project.data.timetable_revision !== ticket.timetableRevision
          ) {
            clearSchedulingJobTicket(projectId, ticket.jobId);
            setSchedulingTicket(null);
            setSchedulingError(
              new Error("项目已发生变化，本次排课未提交，请按当前条件重新排课")
            );
            setSchedulingOpen(true);
            return;
          }
          status = await repository.submitScheduling<GenerateResult>(
            projectId,
            ticket.jobId,
            ticket.accessToken,
            ticket.searchMode,
            ticket.alternativeHistory,
            controller.signal
          );
        }
        if (cancelled) return;
        if (!ticket.submissionConfirmed) {
          try {
            writeSchedulingJobTicket(projectId, {
              ...ticket,
              submissionConfirmed: true
            });
          } catch {
            // The original ticket remains sufficient to recover by querying first.
          }
        }
        setSchedulingJob(status);
        setSchedulingError(null);
        if (status.status === "QUEUED" || status.status === "RUNNING") {
          retryTimer = window.setTimeout(() => void poll(), 1500);
          return;
        }
        setSchedulingOpen(true);
        if (status.status === "FAILED") {
          if (await restoreFallback("寻找其他备选时任务未完成，继续展示上一份备选。")) {
            return;
          }
          return;
        }
        if (!status.response) {
          throw new Error("排课任务已结束，但没有返回可处理的结果");
        }
        if (handledSchedulingJob.current === ticket.jobId) return;
        handledSchedulingJob.current = ticket.jobId;
        const terminalResult = status.response.result;
        if (ticket.operation === "PRECHECK") {
          if (status.response.mutated) {
            throw new Error("预检任务意外修改了课表，请刷新后重试");
          }
          if (terminalResult.outcome === "FEASIBLE") {
            const record = await repository.savePrecheckCandidate(
              projectId,
              ticket.jobId,
              terminalResult as unknown as SchedulingPrecheckResult,
              ticket.alternativeHistory ?? null
            );
            if (cancelled) return;
            setPrecheckRecord(record);
            setPrecheckRecordLoaded(true);
            setGenerateResult(terminalResult);
            setCandidateNotice(null);
            for (const item of [ticket, ticket.fallback].filter(Boolean) as Array<{
              jobId: string;
              accessToken: string;
            }>) {
              try {
                await repository.acknowledgeScheduling(
                  item.jobId,
                  item.accessToken,
                  controller.signal
                );
              } catch {
                // 候选已安全落盘，队列密文可按七天保留规则自动清理。
              }
            }
            clearSchedulingJobTicket(projectId, ticket.jobId);
            setSchedulingTicket(null);
            return;
          }
          if (terminalResult.outcome === "INFEASIBLE") {
            if (!terminalResult.conflict_certificate?.minimality.verified) {
              throw new Error("排课服务没有返回可复核的确定矛盾，请重新预检");
            }
            setGenerateResult(terminalResult);
            return;
          }
          if (terminalResult.outcome === "UNKNOWN") {
            setGenerateResult(terminalResult);
            return;
          }
          throw new Error("预检任务返回了无法识别的结果，请刷新后重试");
        }
        if (terminalResult.outcome === "RELAXED_CANDIDATE") {
          if (status.response.mutated || !terminalResult.candidate) {
            throw new Error("排课服务返回了无效的备选课表状态");
          }
          setGenerateResult(terminalResult);
          setCandidateNotice(null);
          setReplanningCandidate(false);
          if (ticket.fallback) {
            try {
              await repository.acknowledgeScheduling(
                ticket.fallback.jobId,
                ticket.fallback.accessToken,
                controller.signal
              );
            } catch {
              // 旧候选会在七天后自动清理。
            }
            const settledTicket = { ...ticket, fallback: undefined };
            writeSchedulingJobTicket(projectId, settledTicket);
            setSchedulingTicket(settledTicket);
          }
          return;
        }
        if (
          terminalResult.outcome === "NO_ACCEPTABLE_ALTERNATIVE" &&
          (await restoreFallback("没有找到另一份可接受的备选，继续展示上一份备选。"))
        ) {
          return;
        }
        setSavingSchedule(status.response.mutated);
        try {
          const result = await repository.applySchedulingResponse<GenerateResult>(
            projectId,
            status.response
          );
          if (cancelled) return;
          setGenerateResult(result);
          setReplanningCandidate(false);
          if (result.outcome === "GENERATED") {
            await repository.acknowledgeScheduling(
              ticket.jobId,
              ticket.accessToken,
              controller.signal
            );
            clearSchedulingJobTicket(projectId, ticket.jobId);
            setSavingSchedule(false);
            setSchedulingTicket(null);
            await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
          }
        } catch (error) {
          handledSchedulingJob.current = null;
          if (error instanceof ProjectRevisionConflictError) {
            try {
              await repository.acknowledgeScheduling(
                ticket.jobId,
                ticket.accessToken
              );
            } catch {
              // The encrypted terminal result will expire automatically in seven days.
            }
            clearSchedulingJobTicket(projectId, ticket.jobId);
            setSavingSchedule(false);
            setSchedulingTicket(null);
            setSchedulingError(
              new Error("排课期间项目已发生变化，结果没有覆盖当前数据，请重新排课")
            );
            return;
          }
          throw error;
        } finally {
          if (!cancelled) setSavingSchedule(false);
        }
      } catch (error) {
        if (
          cancelled ||
          (error instanceof DOMException && error.name === "AbortError")
        ) {
          return;
        }
        setSchedulingError(error);
        setSchedulingOpen(true);
        handledSchedulingJob.current = null;
        if (error instanceof SchedulingPrecheckStorageError) return;
        retryTimer = window.setTimeout(() => void poll(), 3000);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      controller.abort();
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [
    project.data?.condition_revision,
    project.data?.timetable_revision,
    projectId,
    queryClient,
    schedulingTicket?.jobId,
    schedulingRetryVersion
  ]);

  useEffect(() => {
    if (!repairTicket || !project.data) return;
    const ticket = repairTicket;
    if (ticket.conditionRevision !== project.data.condition_revision) {
      clearRepairSessionTicket(projectId, ticket.sessionId);
      setRepairTicket(null);
      setRepairSession(null);
      setRepairError(
        new Error("排课条件已变化，原调整方案已失效，请按当前条件重新排课")
      );
      setSchedulingOpen(true);
      void getProjectRepository().then(async (repository) => {
        try {
          await repository.invalidateRepairSession(
            ticket.sessionId,
            ticket.accessToken
          );
        } catch {
          // 服务端仍会在下一次操作时核对条件，并在七天后清理会话。
        }
      });
      return;
    }
    let cancelled = false;
    let retryTimer: number | null = null;
    const controller = new AbortController();

    const submitPending = async (
      repository: Awaited<ReturnType<typeof getProjectRepository>>
    ): Promise<RepairSessionStatus> => {
      const pending = ticket.pendingRequest;
      if (!pending) {
        return repository.repairSession(
          ticket.sessionId,
          ticket.accessToken,
          controller.signal
        );
      }
      if (pending.type === "CREATE") {
        return repository.createRepairSession(
          ticket.sourceStrictJobId,
          ticket.sessionId,
          ticket.roundId,
          ticket.jobId,
          ticket.accessToken,
          controller.signal
        );
      }
      if (pending.type === "PARTIAL") {
        return repository.createPartialRepairRound(
          ticket.sessionId,
          ticket.accessToken,
          ticket.roundId,
          ticket.jobId,
          controller.signal
        );
      }
      return repository.createRepairRound(
        ticket.sessionId,
        ticket.accessToken,
        {
          round_id: ticket.roundId,
          job_id: ticket.jobId,
          previous_round_id: pending.previousRoundId,
          action: pending.action,
          locked_repair_keys: pending.lockedRepairKeys
        },
        controller.signal
      );
    };

    const poll = async (): Promise<void> => {
      let observedStatus: RepairSessionStatus | null = null;
      try {
        const repository = await getProjectRepository();
        let status: RepairSessionStatus;
        try {
          status = await repository.repairSession(
            ticket.sessionId,
            ticket.accessToken,
            controller.signal
          );
          observedStatus = status;
          if (
            ticket.pendingRequest &&
            status.current_round?.round_id !== ticket.roundId
          ) {
            status = await submitPending(repository);
          }
        } catch (error) {
          if (
            ticket.pendingRequest?.type !== "CREATE" ||
            !(error instanceof ApiError) ||
            error.code !== "REPAIR_SESSION_NOT_FOUND"
          ) {
            throw error;
          }
          status = await submitPending(repository);
        }
        if (cancelled) return;
        if (
          ticket.pendingRequest &&
          status.current_round?.round_id === ticket.roundId
        ) {
          const settled = { ...ticket, pendingRequest: null };
          writeRepairSessionTicket(projectId, settled);
          setRepairTicket(settled);
        }
        setRepairSession(status);
        setRepairError(null);
        setRepairBusy(false);
        if (schedulingTicket?.jobId === ticket.sourceStrictJobId) {
          try {
            await repository.acknowledgeScheduling(
              ticket.sourceStrictJobId,
              ticket.accessToken,
              controller.signal
            );
          } catch {
            // Strict proof has already been copied into the encrypted session.
          }
          clearSchedulingJobTicket(projectId, ticket.sourceStrictJobId);
          setSchedulingTicket(null);
          setSchedulingJob(null);
          setGenerateResult(null);
        }
        if (
          status.current_round?.status === "PENDING" ||
          status.current_round?.status === "RUNNING"
        ) {
          retryTimer = window.setTimeout(() => void poll(), 1500);
        }
      } catch (error) {
        if (
          cancelled ||
          (error instanceof DOMException && error.name === "AbortError")
        ) {
          return;
        }
        if (
          observedStatus?.current_round &&
          ticket.pendingRequest &&
          isRepairSubmissionConflict(error)
        ) {
          const restoredTicket = {
            ...ticket,
            roundId: observedStatus.current_round.round_id,
            jobId: observedStatus.current_round.job_id,
            pendingRequest: null,
            applicationId: null
          };
          writeRepairSessionTicket(projectId, restoredTicket);
          setRepairTicket(restoredTicket);
          setRepairSession(observedStatus);
          setRepairError(null);
          setRepairBusy(false);
          return;
        }
        if (
          error instanceof ApiError &&
          error.code === "REPAIR_SESSION_NOT_FOUND"
        ) {
          clearRepairSessionTicket(projectId, ticket.sessionId);
          setRepairTicket(null);
          setRepairSession(null);
          setRepairError(new Error("调整方案已过期，请重新发起严格排课"));
          setRepairBusy(false);
          return;
        }
        setRepairError(error);
        setRepairBusy(false);
        retryTimer = window.setTimeout(() => void poll(), 3000);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      controller.abort();
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [
    project.data?.condition_revision,
    projectId,
    repairTicket?.sessionId,
    repairTicket?.roundId,
    repairTicket?.pendingRequest?.type,
    schedulingTicket?.jobId
  ]);

  useEffect(() => {
    if (!project.data || !precheckRecordLoaded) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("action") !== "schedule") return;
    url.searchParams.delete("action");
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
    setGenerateResult(null);
    startScheduling();
  }, [project.data, precheckRecordLoaded]);

  if (project.isLoading) {
    return <main className="page-shell"><p className="state-message">正在加载课表…</p></main>;
  }
  if (!project.data || project.isError) {
    return (
      <main className="page-shell">
        <a className="back-link" href="/projects">← 返回课表项目</a>
        <p className="error-message">课表项目不存在或暂时无法读取。</p>
      </main>
    );
  }

  const data = project.data;
  const counts = data.module_counts;
  const foundationFacts = [
    { key: "courses", path: "courses", count: counts.courses ?? 0 },
    { key: "teachers", path: "teachers", count: counts.teachers ?? 0 },
    { key: "classes", path: "classes", count: counts.classes ?? 0 },
    { key: "time_slots", path: "time_slots", count: counts.time_slots ?? 0 }
  ];
  const foundationReady = foundationFacts.every((fact) => fact.count > 0);
  const firstMissing = foundationFacts.find((fact) => fact.count === 0);
  const planReady = data.planning_stage === "DETAIL";
  const repairRoundStatus = repairSession?.current_round?.status;
  const repairNoResult = isRepairNoResultStatus(repairRoundStatus);
  const schedulingInProgress =
    repairTicket !== null
      ? repairSession?.current_round?.status === "PENDING" ||
        repairSession?.current_round?.status === "RUNNING" ||
        repairSession === null
      : schedulingTicket !== null &&
        (schedulingJob === null ||
          schedulingJob.status === "QUEUED" ||
          schedulingJob.status === "RUNNING");
  const schedulingDisabled =
    !schedulingTicket &&
    !repairTicket &&
    (data.conditions_locked_at !== null ||
      data.planning_stage !== "DETAIL" ||
      !precheckRecordLoaded);
  const scheduleLabel = repairTicket
    ? schedulingInProgress
      ? "查看调整进度"
      : "查看调整方案"
    : schedulingTicket
    ? schedulingInProgress
      ? "查看预检进度"
      : "查看预检结果"
    : precheckRecord
    ? "生成课表"
    : precheckRecordLoaded
    ? "开始预检"
    : "正在检查已保存预检";

  function startScheduling(): void {
    if (!project.data) return;
    if (schedulingTicket || repairTicket || precheckRecord) {
      setSchedulingOpen(true);
      return;
    }
    beginScheduling();
  }

  function beginScheduling(): void {
    if (!project.data) return;
    try {
      const ticket = createSchedulingJobTicket(
        project.data.condition_revision,
        project.data.timetable_revision
      );
      writeSchedulingJobTicket(projectId, ticket);
      handledSchedulingJob.current = null;
      setSchedulingJob(null);
      setSchedulingError(null);
      setCandidateNotice(null);
      setGenerateResult(null);
      setSchedulingTicket(ticket);
      setSchedulingOpen(true);
    } catch (error) {
      setSchedulingError(error);
      setSchedulingOpen(true);
    }
  }

  function retrySchedulingRequest(): void {
    if (!schedulingTicket) return;
    handledSchedulingJob.current = null;
    setSchedulingError(null);
    setSchedulingRetryVersion((version) => version + 1);
  }

  async function restartPrecheck(): Promise<void> {
    if (!project.data) return;
    const ticket = schedulingTicket;
    if (ticket) {
      try {
        const repository = await getProjectRepository();
        await repository.acknowledgeScheduling(ticket.jobId, ticket.accessToken);
      } catch {
        // 终态密文会按七天保留规则自动清理。
      }
      clearSchedulingJobTicket(projectId, ticket.jobId);
    }
    handledSchedulingJob.current = null;
    setSchedulingTicket(null);
    setSchedulingJob(null);
    setGenerateResult(null);
    setSchedulingError(null);
    beginScheduling();
  }

  async function generatePrecheckedSchedule(): Promise<void> {
    if (!precheckRecord) return;
    setGeneratingPrechecked(true);
    setSchedulingError(null);
    try {
      const repository = await getProjectRepository();
      const result = await repository.applyPrecheckedSchedule<GenerateResult>(projectId);
      setPrecheckRecord(null);
      setGenerateResult(result);
      setSchedulingJob(null);
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(projectId)
      });
    } catch (error) {
      setSchedulingError(error);
    } finally {
      setGeneratingPrechecked(false);
    }
  }

  async function closeScheduling(): Promise<void> {
    if (repairTicket) {
      setSchedulingOpen(false);
      window.setTimeout(() => schedulingProgressNoticeRef.current?.focus(), 0);
      return;
    }
    const finalResult = generateResult ?? schedulingJob?.response?.result;
    if (
      finalResult?.outcome === "RELAXED_CANDIDATE" ||
      finalResult?.outcome === "INFEASIBLE" ||
      (finalResult?.outcome === "FEASIBLE" && !precheckRecord)
    ) {
      setSchedulingOpen(false);
      return;
    }
    if (
      schedulingTicket &&
      schedulingJob &&
      (schedulingJob.status === "COMPLETED" || schedulingJob.status === "FAILED")
    ) {
      try {
        const repository = await getProjectRepository();
        await repository.acknowledgeScheduling(
          schedulingTicket.jobId,
          schedulingTicket.accessToken
        );
      } catch (error) {
        setSchedulingError(error);
        return;
      }
      clearSchedulingJobTicket(projectId, schedulingTicket.jobId);
      setSchedulingTicket(null);
    }
    setSchedulingOpen(false);
    window.setTimeout(() => schedulingProgressNoticeRef.current?.focus(), 0);
  }

  async function checkConflictConditions(): Promise<void> {
    if (!project.data || !repairTicket || !repairNoResult) return;
    const ticket = repairTicket;
    setRepairBusy(true);
    try {
      const repository = await getProjectRepository();
      await repository.invalidateRepairSession(
        ticket.sessionId,
        ticket.accessToken
      );
    } catch {
      // 当前轮次已结束；服务端会在七天后清理无法主动失效的会话。
    }
    clearRepairSessionTicket(projectId, ticket.sessionId);
    setRepairTicket(null);
    setRepairSession(null);
    setRepairError(null);
    setRepairBusy(false);
    beginScheduling();
  }

  function startNextRepairRound(
    action: "RETRY" | "REJECT_AND_RETRY",
    lockedRepairKeys: string[] = []
  ): void {
    if (!repairTicket || !repairSession?.current_round) return;
    try {
      const next = nextRepairRoundTicket(repairTicket, {
        type: "ROUND",
        previousRoundId: repairSession.current_round.round_id,
        action,
        lockedRepairKeys
      });
      writeRepairSessionTicket(projectId, next);
      setRepairTicket(next);
      setRepairSession(null);
      setRepairError(null);
      setRepairBusy(true);
    } catch (error) {
      setRepairError(error);
    }
  }

  async function acceptRepair(): Promise<void> {
    if (!repairTicket || !repairSession?.current_round?.candidate) return;
    const partial =
      repairSession.current_round.candidate.completeness === "INCOMPLETE";
    const ticket = repairTicket.applicationId
      ? repairTicket
      : { ...repairTicket, applicationId: crypto.randomUUID() };
    try {
      writeRepairSessionTicket(projectId, ticket);
      setRepairTicket(ticket);
      setRepairBusy(true);
      setRepairError(null);
      const repository = await getProjectRepository();
      await repository.acceptRepairCandidate<GenerateResult>(
        projectId,
        ticket.sessionId,
        repairSession.current_round.round_id,
        ticket.applicationId as string,
        ticket.accessToken
      );
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(projectId)
      });
      if (partial) {
        setRepairSession(
          await repository.repairSession(ticket.sessionId, ticket.accessToken)
        );
        setRepairBusy(false);
      } else {
        clearRepairSessionTicket(projectId, ticket.sessionId);
        setRepairTicket(null);
        setRepairSession(null);
        setRepairBusy(false);
        setSchedulingOpen(false);
      }
    } catch (error) {
      setRepairError(error);
      setRepairBusy(false);
    }
  }

  async function replanCandidate(): Promise<void> {
    const candidate = generateResult?.candidate;
    const history = candidate?.alternative_history as
      | SchedulingAlternativeHistory
      | null
      | undefined;
    if (!project.data || !schedulingTicket || !candidate || !history) {
      setSchedulingError(new Error("当前备选缺少重排历史，请重新开始排课"));
      return;
    }
    setReplanningCandidate(true);
    setSchedulingError(null);
    try {
      const repository = await getProjectRepository();
      const nextTicket = createSchedulingJobTicket(
        project.data.condition_revision,
        project.data.timetable_revision,
        {
          jobId: schedulingTicket.jobId,
          accessToken: schedulingTicket.accessToken
        }
      );
      nextTicket.alternativeHistory = history;
      const status = await repository.submitScheduling<GenerateResult>(
        projectId,
        nextTicket.jobId,
        nextTicket.accessToken,
        nextTicket.searchMode,
        history
      );
      const confirmedTicket = {
        ...nextTicket,
        submissionConfirmed: true
      };
      writeSchedulingJobTicket(projectId, confirmedTicket);
      handledSchedulingJob.current = null;
      setSchedulingTicket(confirmedTicket);
      setSchedulingJob(status);
      setGenerateResult(null);
      setCandidateNotice(null);
    } catch (error) {
      setReplanningCandidate(false);
      setSchedulingError(error);
    }
  }

  async function acceptCandidate(): Promise<void> {
    const candidate = generateResult?.candidate;
    if (!project.data || !schedulingTicket || !candidate) return;
    setAcceptingCandidate(true);
    setSchedulingError(null);
    try {
      const repository = await getProjectRepository();
      const result = await repository.execute<GenerateResult>(
        projectId,
        "scheduling.generate",
        {
          expected_condition_revision: project.data.condition_revision,
          expected_timetable_revision: project.data.timetable_revision,
          search_mode: "STRICT_THEN_MINIMAL_RELAXATION",
          candidate
        }
      );
      for (const item of [schedulingTicket, schedulingTicket.fallback].filter(
        Boolean
      ) as Array<{ jobId: string; accessToken: string }>) {
        try {
          await repository.acknowledgeScheduling(item.jobId, item.accessToken);
        } catch {
          // 已保存候选后，旧加密结果可按七天保留规则自动清理。
        }
      }
      clearSchedulingJobTicket(projectId, schedulingTicket.jobId);
      setSchedulingTicket(null);
      setSchedulingJob(null);
      setGenerateResult(result);
      setCandidateNotice(null);
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    } catch (error) {
      setSchedulingError(error);
    } finally {
      setAcceptingCandidate(false);
    }
  }

  // 当前建议：唯一主动作，全部从现有事实推导，优先级自上而下。
  let focus: FocusSuggestion;
  if (data.needs_replan) {
    focus = {
      title: "条件已变化，需要重新排课",
      primary: "schedule",
      secondary: {
        href: `/projects/${projectId}/timetable/classes`,
        label: "查看现有课表"
      }
    };
  } else if (data.has_timetable) {
    focus = {
      title: "课表已生成，可以微调与导出",
      primary: {
        href: `/projects/${projectId}/timetable/classes`,
        label: "进入班级课表微调"
      },
      secondary: {
        href: `/projects/${projectId}/timetable/teachers`,
        label: "查看教师视角"
      }
    };
  } else if (!foundationReady && firstMissing) {
    focus = {
      title: "先补齐基础资料",
      primary: {
        href: `/projects/${projectId}/${firstMissing.path}`,
        label: "去补齐基础资料"
      }
    };
  } else if (!planReady) {
    focus = {
      title: "基础资料就绪，继续编制基础规则",
      primary: {
        href: `/projects/${projectId}/plans`,
        label: "继续编制基础规则"
      }
    };
  } else {
    focus = {
      title: precheckRecord
        ? "预检已通过，可以生成正式课表"
        : "任课计划已就绪，先预检全部条件",
      primary: "schedule",
      secondary: {
        href: `/projects/${projectId}/plans`,
        label: "先校阅详细计划"
      }
    };
  }

  const journeySteps: JourneyStep[] = [
    {
      title: "基础资料",
      note: foundationReady
        ? "课程、教师、班级、节次已录入"
        : "还有基础资料未录入",
      done: foundationReady
    },
    {
      title: "任课计划",
      note: planReady
        ? `已生成 ${counts.plans ?? 0} 条详细计划`
        : "编制基础规则并生成详细计划",
      done: planReady
    },
    {
      title: "生成课表",
      note: data.has_timetable
        ? data.needs_replan
          ? "条件已变化，需重新排课"
          : "已生成完整课表"
        : precheckRecord
        ? "预检通过，等待生成正式课表"
        : "任课计划就绪后先执行预检",
      done: data.has_timetable && !data.needs_replan
    },
    {
      title: "微调导出",
      note: data.has_timetable ? "微调后可导出 Excel" : "生成课表后开放",
      done: false
    }
  ];
  const currentStep = journeySteps.findIndex((step) => !step.done);

  const moduleLines = [
    {
      icon: "课",
      title: "课程信息",
      fact: `已录入 ${counts.courses ?? 0} 项`,
      href: `/projects/${projectId}/courses`,
      available: true
    },
    {
      icon: "班",
      title: "班级信息",
      fact: `已录入 ${counts.classes ?? 0} 项`,
      href: `/projects/${projectId}/classes`,
      available: true
    },
    {
      icon: "师",
      title: "教师信息",
      fact: `已录入 ${counts.teachers ?? 0} 项`,
      href: `/projects/${projectId}/teachers`,
      available: true
    },
    {
      icon: "室",
      title: "功能教室信息",
      fact: `已录入 ${counts.room_types ?? 0} 项`,
      href: `/projects/${projectId}/rooms`,
      available: true
    },
    {
      icon: "时",
      title: "课程表时间",
      fact: `已录入 ${counts.time_slots ?? 0} 项`,
      href: `/projects/${projectId}/time_slots`,
      available: true
    },
    {
      icon: "限",
      title: "不排课时间",
      fact: `已设置 ${counts.unavailability ?? 0} 项`,
      href: `/projects/${projectId}/unavailability`,
      available: true
    },
    {
      icon: "任",
      title: "任课计划",
      fact: planReady
        ? `已录入 ${counts.plans ?? 0} 项`
        : `${counts.plan_requirements ?? 0} 需求 · ${counts.plan_allocations ?? 0} 分配`,
      href: `/projects/${projectId}/plans`,
      available: true
    },
    {
      icon: "表",
      title: "班级课程表",
      fact: data.has_timetable ? "进入" : "尚未排课",
      href: `/projects/${projectId}/timetable/classes`,
      available: data.has_timetable
    },
    {
      icon: "览",
      title: "教师课程表",
      fact: data.has_timetable ? "进入" : "尚未排课",
      href: `/projects/${projectId}/timetable/teachers`,
      available: data.has_timetable
    },
    {
      icon: "室",
      title: "功能教室课表",
      fact: data.has_timetable ? "进入" : "尚未排课",
      href: `/projects/${projectId}/timetable/rooms`,
      available: data.has_timetable
    }
  ];

  return (
    <main className="page-shell project-home">
      <ContextHeader
        projectId={projectId}
        eyebrow="项目总览"
        title={data.name}
        backTo={{ href: "/projects", label: "全部课表项目" }}
        actions={
          <div className="header-actions">
            <button
              className="secondary-button danger-text"
              type="button"
              disabled={demoClear.pending || demoData.isPending}
              onClick={() => {
                setDemoMessage(null);
                demoClear.start();
              }}
            >
              {demoClear.previewMutation.isPending ? "正在统计…" : "一键清除模拟数据"}
            </button>
            <button
              className="demo-data-button"
              type="button"
              disabled={demoData.isPending || demoClear.pending}
              onClick={() => {
                demoData.reset();
                setDemoMessage(null);
                demoData.mutate(false);
              }}
            >
              {demoData.isPending ? "正在填入…" : "使用模拟数据"}
            </button>
          </div>
        }
      />

      {demoMessage ? <p className="demo-data-message" role="status">{demoMessage}</p> : null}
      {demoData.isError && !demoConfirmOpen ? (
        <p className="error-message" role="alert">
          {demoData.error instanceof Error ? demoData.error.message : "模拟数据填入失败"}
        </p>
      ) : null}
      {demoClear.visibleError ? (
        <p className="error-message" role="alert">
          {demoClear.visibleError instanceof Error
            ? demoClear.visibleError.message
            : "模拟数据清理预估失败"}
        </p>
      ) : null}
      {data.accepted_relaxation_count > 0 ? (
        <details className="accepted-relaxation-notice">
          <summary>
            本课表采用时放宽了 {data.accepted_relaxation_count} 项条件
            <span>查看明细</span>
          </summary>
          <ul>
            {data.accepted_relaxations.map((item) => (
              <li key={item.event_id}>
                <strong>{item.message_snapshot}</strong>
                <span>原要求：{item.original_value}</span>
                <span>实际安排：{item.actual_value}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {(schedulingTicket || repairTicket) && !schedulingOpen ? (
        <button
          ref={schedulingProgressNoticeRef}
          className="scheduling-progress-notice"
          type="button"
          onClick={() => setSchedulingOpen(true)}
        >
          <span>
            <strong>
              {repairTicket
                ? repairRoundStatus === "FOUND"
                  ? "调整方案已找到"
                  : repairNoResult
                  ? "本轮没有找到可行的解决方案"
                  : repairRoundStatus === "CANCELLED"
                  ? "本轮任务已取消"
                  : "正在寻找调整方案"
                : schedulingStatusTitle(
                    schedulingJob,
                    savingSchedule,
                    generateResult,
                    precheckRecord !== null,
                    generatingPrechecked
                  )}
            </strong>
            <small>
              {schedulingInProgress
                ? "任务会在后台继续，可随时回来查看"
                : repairNoResult
                ? "可以重新检查当前排课条件"
                : "可随时回来查看结果"}
            </small>
          </span>
          <span aria-hidden="true">
            {schedulingInProgress ? "查看进度 →" : "查看结果 →"}
          </span>
        </button>
      ) : null}

      <div className="journey-layout">
        <nav className="journey-rail" aria-label="排课旅程进度">
          {journeySteps.map((step, index) => (
            <div
              className={`journey-step${step.done ? " done" : ""}${!step.done && index === currentStep ? " current" : ""}`}
              key={step.title}
              {...(!step.done && index === currentStep ? { "aria-current": "step" as const } : {})}
            >
              <span className="step-dot" aria-hidden="true">
                {step.done ? "✓" : index + 1}
              </span>
              <div>
                <strong>{step.title}</strong>
                <small>{step.note}</small>
              </div>
            </div>
          ))}
        </nav>

        <section className="focus-panel" aria-label="当前建议与全部模块">
          <div className="focus-hero">
            <p className="focus-kicker">当前建议</p>
            <h2>{focus.title}</h2>
            <div className="focus-actions">
              {focus.primary === "schedule" ? (
                <button
                  className="solid-button"
                  type="button"
                  disabled={schedulingDisabled}
                  onClick={startScheduling}
                >
                  {scheduleLabel}
                </button>
              ) : (
                <a className="solid-button" href={focus.primary.href}>
                  {focus.primary.label}
                </a>
              )}
              {focus.secondary ? (
                <a className="ghost-button" href={focus.secondary.href}>
                  {focus.secondary.label}
                </a>
              ) : null}
              {focus.primary !== "schedule" ? (
                <button
                  className="ghost-button"
                  type="button"
                  disabled={schedulingDisabled}
                  onClick={startScheduling}
                >
                  {scheduleLabel}
                </button>
              ) : null}
            </div>
          </div>

          <div className="module-lines">
            {moduleLines.map((line) =>
              line.available ? (
                <a className="module-line" href={line.href} key={line.title}>
                  <span className="module-icon" aria-hidden="true">{line.icon}</span>
                  <span className="module-line-main">
                    <strong>{line.title}</strong>
                  </span>
                  <span className="module-line-fact">{line.fact}</span>
                </a>
              ) : (
                <div className="module-line is-disabled" key={line.title} aria-disabled="true">
                  <span className="module-icon" aria-hidden="true">{line.icon}</span>
                  <span className="module-line-main">
                    <strong>{line.title}</strong>
                  </span>
                  <span className="module-line-fact">{line.fact}</span>
                </div>
              )
            )}
          </div>
        </section>

        <aside className="side-summary" aria-label="项目事实摘要">
          <h3>项目事实</h3>
          <div className="fact">
            <span>班级</span>
            <strong>{counts.classes ?? 0}</strong>
          </div>
          <div className="fact">
            <span>教师</span>
            <strong>{counts.teachers ?? 0}</strong>
          </div>
          <div className="fact">
            <span>课程</span>
            <strong>{counts.courses ?? 0}</strong>
          </div>
          <div className="fact">
            <span>详细任课计划</span>
            <strong>{counts.plans ?? 0}</strong>
          </div>
          <div className="fact">
            <span>条件修订号</span>
            <strong>{data.condition_revision}</strong>
          </div>
          <div className="fact">
            <span>排课条件</span>
            <span className={`pill${data.conditions_locked_at ? " warn" : ""}`}>
              {data.conditions_locked_at ? "已锁定" : "可编辑"}
            </span>
          </div>
          {data.needs_replan ? (
            <div className="side-issue" role="alert">
              <h2 id="replan-title">需重新排课</h2>
              <ul>
                {data.replan_reasons.map((reason) => (
                  <li key={reason.id}>{reason.message_snapshot}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {data.conditions_locked_at ? (
            <p className="side-note">
              条件已随排课完成锁定。想尝试另一套条件或排法，请从首页复制课表后继续。
            </p>
          ) : null}
        </aside>
      </div>

      {schedulingOpen && repairTicket ? (
        <RepairSessionModal
          session={repairSession}
          ticket={repairTicket}
          busy={repairBusy}
          error={repairError}
          onClose={() => void closeScheduling()}
          onCheckConflicts={() => void checkConflictConditions()}
          onRetry={() => startNextRepairRound("RETRY")}
          onReject={(keys) => startNextRepairRound("REJECT_AND_RETRY", keys)}
          onAccept={() => void acceptRepair()}
        />
      ) : schedulingOpen ? (
        <SchedulingModal
          projectId={projectId}
          job={schedulingJob}
          result={generateResult}
          precheckRecord={precheckRecord}
          saving={savingSchedule}
          generatingPrechecked={generatingPrechecked}
          acceptingCandidate={acceptingCandidate}
          replanningCandidate={replanningCandidate}
          candidateNotice={candidateNotice}
          error={schedulingError}
          onRetry={schedulingTicket ? retrySchedulingRequest : undefined}
          onClose={() => void closeScheduling()}
          onAcceptCandidate={() => void acceptCandidate()}
          onReplanCandidate={() => void replanCandidate()}
          onGeneratePrechecked={() => void generatePrecheckedSchedule()}
          onRestartPrecheck={() => void restartPrecheck()}
        />
      ) : null}
      {demoConfirmOpen ? (
        <ConfirmModal
          title="使用模拟数据并重新开始？"
          description="这会替换六项基础资料，清空任课计划、可用节次、课程相邻和不相邻设置，并开放条件编辑；已经生成和手动调整过的课表会完整保留。"
          dangerLabel="确认使用模拟数据"
          onClose={() => {
            demoData.reset();
            setDemoConfirmOpen(false);
          }}
          onConfirm={() => demoData.mutate(true)}
          pending={demoData.isPending}
          error={
            demoData.isError &&
            !(demoData.error instanceof ApiError && demoData.error.code === "CONFIRMATION_REQUIRED")
              ? demoData.error
              : null
          }
        />
      ) : null}
      {demoClear.preview ? (
        <ConfirmModal
          title="清除基础模拟数据？"
          description={`${demoClearSummary(demoClear.preview)}。只会清理来源明确、从未人工改动且未被其他数据引用的基础模拟记录；现有课表和全部历史版本保持不变。`}
          dangerLabel="确认清理"
          onClose={demoClear.close}
          onConfirm={demoClear.confirm}
          pending={demoClear.clearMutation.isPending}
          error={demoClear.clearMutation.error}
        />
      ) : null}
    </main>
  );
}

function RepairSessionModal({
  session,
  ticket,
  busy,
  error,
  onClose,
  onCheckConflicts,
  onRetry,
  onReject,
  onAccept
}: {
  session: RepairSessionStatus | null;
  ticket: RepairSessionTicket;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onCheckConflicts: () => void;
  onRetry: () => void;
  onReject: (lockKeys: string[]) => void;
  onAccept: () => void;
}): React.JSX.Element {
  const dialogRef = useDialogFocus(onClose, true);
  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="modal-card scheduling-modal relaxed-candidate-modal repair-session-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="repair-session-title"
      >
        <h2 id="repair-session-title">
          {session?.current_round?.candidate
            ? session.current_round.candidate.completeness === "COMPLETE"
              ? "找到一套可行调整方案"
              : "最大可排参考方案"
            : "寻找可行调整方案"}
        </h2>
        <RepairSessionPanel
          session={session}
          accessToken={ticket.accessToken}
          busy={busy}
          error={error}
          onClose={onClose}
          onCheckConflicts={onCheckConflicts}
          onRetry={onRetry}
          onReject={onReject}
          onAccept={onAccept}
        />
      </section>
    </div>
  );
}

function SchedulingModal({
  projectId,
  job,
  result,
  precheckRecord,
  saving,
  generatingPrechecked,
  acceptingCandidate,
  replanningCandidate,
  candidateNotice,
  error,
  onRetry,
  onClose,
  onAcceptCandidate,
  onReplanCandidate,
  onGeneratePrechecked,
  onRestartPrecheck
}: {
  projectId: string;
  job: SchedulingJobView | null;
  result: GenerateResult | null;
  precheckRecord: SchedulingPrecheckRecordV1 | null;
  saving: boolean;
  generatingPrechecked: boolean;
  acceptingCandidate: boolean;
  replanningCandidate: boolean;
  candidateNotice: string | null;
  error: unknown;
  onRetry?: () => void;
  onClose: () => void;
  onAcceptCandidate: () => void;
  onReplanCandidate: () => void;
  onGeneratePrechecked: () => void;
  onRestartPrecheck: () => void;
}): React.JSX.Element {
  const dialogRef = useDialogFocus(onClose, true);
  const finalResult = result ?? job?.response?.result ?? null;
  const candidate = finalResult?.candidate;
  if (finalResult?.outcome === "RELAXED_CANDIDATE" && candidate) {
    return (
      <div className="modal-backdrop" role="presentation">
        <section
          ref={dialogRef}
          className="modal-card scheduling-modal relaxed-candidate-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="scheduling-title"
        >
          <h2 id="scheduling-title">
            严格条件未找到完整排法，系统找到一份备选课表
          </h2>
          <RelaxedCandidatePanel
            projectId={projectId}
            candidate={candidate}
            scheduledLessons={finalResult.scheduled_lessons ?? candidate.assignments.length}
            requiredLessons={finalResult.required_lessons ?? candidate.assignments.length}
            accepting={acceptingCandidate}
            replanning={replanningCandidate}
            notice={candidateNotice}
            error={error}
            onAccept={onAcceptCandidate}
            onReplan={onReplanCandidate}
            onClose={onClose}
          />
        </section>
      </div>
    );
  }
  const success = finalResult?.outcome === "GENERATED";
  const precheckFeasible = precheckRecord !== null;
  const unknown = finalResult?.outcome === "UNKNOWN";
  const solverProvedInfeasible = unknown && finalResult?.solver_status === "INFEASIBLE";
  const legacyUnexplainedInfeasible =
    solverProvedInfeasible &&
    (finalResult?.conflict_analyzer_version ?? 0) < CONFLICT_ANALYZER_VERSION;
  const infeasible =
    finalResult?.outcome === "INFEASIBLE" ||
    finalResult?.outcome === "NO_ALTERNATIVE" ||
    finalResult?.outcome === "NO_ACCEPTABLE_ALTERNATIVE";
  const resultIssues = (finalResult?.issues ?? []) as SchedulingIssue[];
  const failureIssues = schedulingFailureIssues(job?.error ?? null);
  const issues = resultIssues.length ? resultIssues : failureIssues;
  const suggestions = (finalResult?.suggestions ?? []) as SchedulingSuggestion[];
  const certificate = finalResult?.conflict_certificate ?? null;
  const inputInvalid =
    job?.status === "FAILED" && job.error?.code === "SCHEDULING_INPUT_INVALID";
  const title = error && onRetry && !job && !finalResult
    ? "任务提交暂未确认"
    : schedulingStatusTitle(
        job,
        saving,
        finalResult,
        precheckFeasible,
        generatingPrechecked
      );
  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="modal-card wide scheduling-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scheduling-title"
      >
        <div className="scheduling-modal-heading">
          <h2 id="scheduling-title">{title}</h2>
          <button
            className="dialog-close"
            type="button"
            aria-label="关闭预检窗口"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="scheduling-modal-content">
          <div aria-live="polite" aria-atomic="true">
            {!job && !error && !finalResult && !precheckFeasible ? (
              <p>正在安全提交预检任务…</p>
            ) : null}
            {job?.status === "QUEUED" ? (
              <div className="scheduling-queue-state" role="status">
                <p>预检任务已安全提交，正在排队中。</p>
                <strong>
                  {job.estimated_wait_seconds && job.estimated_wait_seconds > 0
                    ? `预计等待约 ${formatDuration(job.estimated_wait_seconds)}`
                    : "预计很快开始"}
                </strong>
                <small>该时间按通常任务估算，复杂任务可能延长。</small>
              </div>
            ) : null}
            {job?.status === "RUNNING" ? (
              <>
                <p>正在验证全部硬条件，并寻找第一份完整可行课表。</p>
                <div className="progress-track" aria-label="预检进行中"><span /></div>
                <p className="field-hint">本轮计算最长 60 秒；不会自动生成或修改正式课表。</p>
              </>
            ) : null}
            {saving ? <p>已找到完整排法，正在安全保存到当前浏览器…</p> : null}
            {generatingPrechecked ? (
              <p>正在复核并生成正式课表，不会再次启动求解器…</p>
            ) : null}
          </div>
          {unknown ? (
            <p>
              {legacyUnexplainedInfeasible
                ? "这份结果由旧版冲突分析器生成。系统当时已经确认没有完整课表，但新版规则可能可以定位出可复核的矛盾；可以重新检查一次，项目数据不会发生变化。"
                : solverProvedInfeasible
                ? "系统已经确认当前条件组合不存在完整课表，但暂时未能生成可复核的最小矛盾说明。重复预检通常不会改变结果，请调整排课条件；项目数据未发生变化。"
                : "本轮在时间上限内既没有找到完整排法，也没有形成可复核的确定矛盾；这不等于条件一定不可排，项目数据未发生变化。"}
            </p>
          ) : null}
          {infeasible && finalResult?.outcome === "NO_ACCEPTABLE_ALTERNATIVE" ? (
            <p className="warning-note" role="alert">
              系统已按从轻到重的顺序尝试备选条件，但仍未找到一份可接受的完整课表。项目数据没有变化。
            </p>
          ) : null}
          {success && finalResult ? (
            <div className="success-panel">
              <strong>已安排全部课程并保存</strong>
              <span>用时 {(finalResult.duration_ms / 1000).toFixed(1)} 秒，可进入班级或教师课表继续微调。</span>
            </div>
          ) : null}
          {precheckFeasible ? (
            <div className="success-panel" role="status">
              <strong>预检通过，完整候选已安全保存</strong>
              <span>
                现在可以生成正式课表；这一步只复核并采用已保存候选，不会再次求解。
              </span>
            </div>
          ) : null}
          {success && finalResult?.course_adjacency_relaxed ? (
            <p className="warning-note" role="status">
              本次排课已按用户确认放宽相邻规则，达成 {finalResult.adjacency_achieved_pairs ?? 0} / {finalResult.adjacency_requested_pairs ?? 0} 对。
            </p>
          ) : null}
          {certificate ? <ConflictCertificateCard certificate={certificate} /> : null}
          {!certificate && issues.length ? (
            <div className="scheduling-feedback">
              <h3>需要处理的问题</h3>
              <ul>
                {issues.map((issue, index) => (
                  <li key={`${issue.code}-${index}`}>
                    <strong>{issue.message}</strong>
                    {issue.field_path ? <small>{fieldPathLabel(issue.field_path)}</small> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {!certificate && suggestions.length ? (
            <div className="scheduling-feedback suggestions">
              <h3>可尝试的修改</h3>
              <ul>
                {suggestions.map((suggestion) => (
                  <li key={`${suggestion.code}-${suggestion.message}`}>
                    {suggestion.message}
                  </li>
                ))}
              </ul>
              <p className="field-hint">建议不会自动修改条件，单独采用一条也不保证一定可排。</p>
            </div>
          ) : null}
          {error ? (
            <p className="error-message" role="alert">
              {errorMessage(error)}
            </p>
          ) : null}
          {job?.status === "FAILED" && job.error && !issues.length ? (
            <p className="error-message" role="alert">{job.error.message}</p>
          ) : null}
          <div className="modal-actions">
            {error && onRetry ? (
              <button
                className="primary-button"
                type="button"
                onClick={onRetry}
              >
                重新尝试
              </button>
            ) : null}
            {precheckFeasible ? (
              <button
                className="primary-button"
                type="button"
                disabled={generatingPrechecked}
                onClick={onGeneratePrechecked}
              >
                {generatingPrechecked ? "正在生成…" : "生成课表"}
              </button>
            ) : null}
            {unknown && (!solverProvedInfeasible || legacyUnexplainedInfeasible) ? (
              <button
                className="primary-button"
                type="button"
                onClick={onRestartPrecheck}
              >
                {legacyUnexplainedInfeasible ? "用新版规则重新检查" : "重新预检"}
              </button>
            ) : null}
            {finalResult?.outcome === "INFEASIBLE" ? (
              <a className="primary-button" href={`/projects/${projectId}/plans`}>
                调整排课条件
              </a>
            ) : null}
            {inputInvalid ? (
              <a className="primary-button" href={`/projects/${projectId}/plans`}>
                调整排课条件
              </a>
            ) : null}
            {unknown ? (
              <a
                className={
                  solverProvedInfeasible && !legacyUnexplainedInfeasible
                    ? "primary-button"
                    : "secondary-button"
                }
                href={`/projects/${projectId}/plans`}
              >
                {solverProvedInfeasible ? "调整排课条件" : "修改条件"}
              </a>
            ) : null}
            {finalResult?.outcome !== "INFEASIBLE" && !inputInvalid ? (
              <button
                className="secondary-button"
                type="button"
                onClick={onClose}
              >
                {success ? "完成" : "关闭"}
              </button>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}

function ConflictCertificateCard({
  certificate
}: {
  certificate: ConflictCertificate;
}): React.JSX.Element {
  return (
    <section className="conflict-certificate" aria-labelledby="conflict-title">
      <h3 id="conflict-title">发现一组确定矛盾</h3>
      <p>{certificate.summary}。</p>
      <ul className="conflict-facts">
        {certificate.facts.map((fact) => (
          <li key={fact.fact_id}>{fact.label}</li>
        ))}
      </ul>
      <div className="conflict-proof" aria-label="矛盾计算">
        <div>
          <span>实际需求</span>
          <strong>
            {certificate.required.value} {certificate.required.unit}
          </strong>
        </div>
        <div>
          <span>可排容量</span>
          <strong>
            {certificate.capacity.value} {certificate.capacity.unit}
          </strong>
        </div>
        <div>
          <span>缺口</span>
          <strong>{certificate.gap}</strong>
        </div>
      </div>
      {certificate.calculation ? (
        <p className="conflict-equation">{certificate.calculation}</p>
      ) : null}
      <p>
        因为 {certificate.required.value} &gt; {certificate.capacity.value}
        ，以上条件本身已经足以证明当前要求不可排。
      </p>
      {certificate.suggestions.length ? (
        <div className="conflict-directions">
          <h4>要解除这组矛盾，至少需要满足以下任一方向</h4>
          <ul>
            {certificate.suggestions.map((suggestion) => (
              <li key={`${suggestion.fact_id}-${suggestion.code}`}>
                {suggestion.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="field-hint">
        以上只表示这组矛盾被解除；修改后仍需重新预检，系统会继续检查其他条件。
      </p>
    </section>
  );
}

function schedulingStatusTitle(
  job: SchedulingJobView | null,
  saving: boolean,
  result?: GenerateResult | null,
  precheckFeasible = false,
  generatingPrechecked = false
): string {
  if (generatingPrechecked) return "正在生成正式课表";
  if (saving) return "正在保存课表";
  const finalResult = result ?? job?.response?.result;
  if (precheckFeasible) return "预检通过，可以生成课表";
  if (finalResult?.outcome === "GENERATED") return "课表已生成并保存";
  if (finalResult?.outcome === "FEASIBLE") return "正在保存预检候选";
  if (finalResult?.outcome === "RELAXED_CANDIDATE") return "已找到一份备选课表";
  if (finalResult?.outcome === "NO_ACCEPTABLE_ALTERNATIVE") {
    return "未找到可接受的备选课表";
  }
  if (finalResult?.outcome === "UNKNOWN") {
    if (finalResult.solver_status !== "INFEASIBLE") return "暂时无法判断";
    return (finalResult.conflict_analyzer_version ?? 0) < CONFLICT_ANALYZER_VERSION
      ? "新版规则可以重新检查矛盾"
      : "确认无完整排法，暂未定位矛盾";
  }
  if (!job) return "正在提交预检任务";
  if (job.status === "QUEUED") return "预检正在排队";
  if (job.status === "RUNNING") return "正在预检排课条件";
  if (job.status === "FAILED") {
    return job.error?.code === "SCHEDULING_INPUT_INVALID"
      ? "排课条件需要调整"
      : "预检请求未完成";
  }
  return "发现确定矛盾";
}

function schedulingFailureIssues(
  error: SchedulingJobView["error"]
): SchedulingIssue[] {
  if (!error) return [];
  const detailed = error.details.issues;
  const source = Array.isArray(detailed) ? detailed : error.field_errors;
  return source.flatMap((value) => {
    if (!isObjectRecord(value) || typeof value.message !== "string") return [];
    return [
      {
        code:
          typeof value.code === "string"
            ? value.code
            : typeof value.type === "string"
              ? value.type
              : error.code,
        message: value.message,
        field_path:
          typeof value.field_path === "string"
            ? value.field_path
            : typeof value.path === "string"
              ? value.path
              : null,
        suggestions: []
      }
    ];
  });
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "排课请求失败，请稍后重试";
}

function isRepairSubmissionConflict(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    (error.code === "REPAIR_ROUND_NOT_AVAILABLE" ||
      error.code === "PARTIAL_ROUND_NOT_AVAILABLE" ||
      error.code === "REPAIR_ROUND_CONFLICT")
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} 秒`;
  return `${Math.ceil(seconds / 60)} 分钟`;
}

function fieldPathLabel(path: string): string {
  if (path === "time_slots") return "课程表时间";
  if (path === "plans") return "任课计划";
  if (
    path.includes("time_preference") ||
    path.includes("available_time_slot_ids")
  ) return "任课计划 · 可排节次";
  if (path.includes("allow_multiple_groups_per_day")) return "任课计划 · 同一天可多节";
  if (path.includes("weekly_lessons")) return "任课计划 · 一周课时";
  return "任课计划";
}
